import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, Observable, of } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface HttpOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  params?:
    | HttpParams
    | {
        [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  readonly apiUrl = environment.apiUrl;

  private readonly http = inject(HttpClient);

  /**
   * Merge headers with skipLoading flag
   * @param skipLoading Whether to skip loading indicator
   * @param options Existing HTTP options
   */
  private mergeHeaders(skipLoading: boolean, options?: HttpOptions): HttpOptions {
    // Only add header if skipLoading is true
    if (!skipLoading) {
      return options || {};
    }

    // Create new HttpHeaders from existing headers (if any) and add X-Skip-Loading
    let headers: HttpHeaders;
    if (options?.headers) {
      // Check if headers is already an HttpHeaders instance
      if (options.headers instanceof HttpHeaders) {
        headers = options.headers.set('X-Skip-Loading', 'true');
      } else {
        // It's a plain object, so we can use the constructor
        headers = new HttpHeaders(options.headers).set('X-Skip-Loading', 'true');
      }
    } else {
      headers = new HttpHeaders().set('X-Skip-Loading', 'true');
    }

    return { ...options, headers };
  }

  /**
   * Perform a GET request
   * @param endpoint API endpoint (will be appended to apiUrl)
   * @param skipLoading Whether to skip loading indicator (default: false)
   * @param options Optional HTTP options (headers, params, etc.)
   */
  get<T>(endpoint: string, skipLoading: boolean = false, options?: HttpOptions): Observable<T> {
    const mergedOptions = this.mergeHeaders(skipLoading, options);
    return this.http.get<T>(`${this.apiUrl}/${endpoint}`, mergedOptions);
  }

  /**
   * Perform a POST request
   * @param endpoint API endpoint (will be appended to apiUrl)
   * @param body Request body
   * @param skipLoading Whether to skip loading indicator (default: false)
   * @param options Optional HTTP options (headers, params, etc.)
   */
  post<T>(
    endpoint: string,
    body: any,
    skipLoading: boolean = false,
    options?: HttpOptions,
  ): Observable<T> {
    const mergedOptions = this.mergeHeaders(skipLoading, options);
    return this.http.post<T>(`${this.apiUrl}/${endpoint}`, body, mergedOptions);
  }

  /**
   * Perform a PUT request
   * @param endpoint API endpoint (will be appended to apiUrl)
   * @param body Request body
   * @param skipLoading Whether to skip loading indicator (default: false)
   * @param options Optional HTTP options (headers, params, etc.)
   */
  put<T>(
    endpoint: string,
    body: any,
    skipLoading: boolean = false,
    options?: HttpOptions,
  ): Observable<T> {
    const mergedOptions = this.mergeHeaders(skipLoading, options);
    return this.http.put<T>(`${this.apiUrl}/${endpoint}`, body, mergedOptions);
  }

  /**
   * Perform a PATCH request
   * @param endpoint API endpoint (will be appended to apiUrl)
   * @param body Request body
   * @param skipLoading Whether to skip loading indicator (default: false)
   * @param options Optional HTTP options (headers, params, etc.)
   */
  patch<T>(
    endpoint: string,
    body: any,
    skipLoading: boolean = false,
    options?: HttpOptions,
  ): Observable<T> {
    const mergedOptions = this.mergeHeaders(skipLoading, options);
    return this.http.patch<T>(`${this.apiUrl}/${endpoint}`, body, mergedOptions);
  }

  /**
   * Perform a DELETE request
   * @param endpoint API endpoint (will be appended to apiUrl)
   * @param skipLoading Whether to skip loading indicator (default: false)
   * @param options Optional HTTP options (headers, params, etc.)
   */
  delete<T>(endpoint: string, skipLoading: boolean = false, options?: HttpOptions): Observable<T> {
    const mergedOptions = this.mergeHeaders(skipLoading, options);
    return this.http.delete<T>(`${this.apiUrl}/${endpoint}`, mergedOptions);
  }

  test() {
    return this.http
      .get(`${this.apiUrl}/category`)
      .pipe(
        catchError((err) => {
          console.error('HTTP Error:', err);
          return of(null);
        }),
      )
      .subscribe();
  }
}
